import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { collectFee, updateFee, fetchFees } from "../../redux/admin/fees.thunk";
import { fetchStudents } from "../../redux/admin/student.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* =====================================================
   SEARCHABLE STUDENT COMBOBOX
   A plain <select> becomes unusable once a school has a
   few hundred students, and it's a poor touch target on
   mobile. This is a lightweight, dependency-free combobox:
   type to filter, tap/click or use arrow keys + Enter to
   pick, Escape or outside-click to close.
===================================================== */
const StudentCombobox = ({ students, value, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(value)),
    [students, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        String(s.admission_number || "").toLowerCase().includes(q)
    );
  }, [students, query]);

  // Keep the visible input text in sync with the selected student when closed
  useEffect(() => {
    if (!open) {
      setQuery(selectedStudent ? `${selectedStudent.admission_number} — ${selectedStudent.name}` : "");
    }
  }, [selectedStudent, open]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  // Keep the active option visible while navigating with arrow keys
  useEffect(() => {
    if (!open || !listRef.current) return;
    const activeEl = listRef.current.children[activeIndex];
    if (activeEl) activeEl.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const commit = (student) => {
    onSelect(student ? String(student.id) : "");
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) commit(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery(selectedStudent ? `${selectedStudent.admission_number} — ${selectedStudent.name}` : "");
    }
  };

  return (
    <div className="mnc-combobox" ref={wrapperRef}>
      <div className={`mnc-combobox-field ${open ? "is-open" : ""}`}>
        <svg className="mnc-combobox-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-3.8-3.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="mnc-field mnc-combobox-input fw-600"
          placeholder="Search by name or admission number…"
          value={query}
          disabled={disabled}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {value && !open && (
          <button
            type="button"
            className="mnc-combobox-clear"
            aria-label="Clear selected student"
            onClick={() => commit(null)}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <ul className="mnc-combobox-list" ref={listRef} role="listbox">
          {filtered.length === 0 && (
            <li className="mnc-combobox-empty">No students match “{query}”</li>
          )}
          {filtered.map((student, index) => (
            <li
              key={student.id}
              role="option"
              aria-selected={String(student.id) === String(value)}
              className={`mnc-combobox-option ${index === activeIndex ? "is-active" : ""} ${String(student.id) === String(value) ? "is-selected" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus so blur doesn't close before click registers
                commit(student);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <span className="mnc-combobox-option-name">{student.name}</span>
              <span className="mnc-combobox-option-adm">{student.admission_number}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Fees = () => {
  const dispatch = useDispatch();

  /* =====================================================
     REDUX STATE
  ===================================================== */
  const { students = [] } = useSelector((state) => state.admin?.students || {});
  const { fees = [] } = useSelector((state) => state.admin?.fees || {});

  /* =====================================================
     LOCAL FORM WORKBENCH STATE
  ===================================================== */
  const initialFormState = {
    id: null, // Track transaction ID for updates
    student_id: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_mode: "cash",
    remarks: "",
    status: "paid"
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* =====================================================
     DATA REFERENCE MASTER
  ===================================================== */
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(formData.student_id)),
    [students, formData.student_id]
  );

  /* =====================================================
     INITIALIZATION LIFE CYCLE
  ===================================================== */
  useEffect(() => {
    dispatch(fetchStudents({ page: 1, limit: 1000, status: "active" }));
    dispatch(fetchFees({ page: 1, limit: 200, search: "" })); // Load recent ledger history for quick-lookup cross checks
  }, [dispatch]);

  /* =====================================================
     INTELLIGENT LOOKUP MATCH ENGINE
  ===================================================== */
  const handleStudentSelection = (studentId) => {
    if (!studentId) {
      handleReset();
      return;
    }

    // Look for an existing transaction matching this student for the target period
    const targetMatch = fees.find(
      (f) => String(f.student_id) === String(studentId) &&
             Number(f.month) === Number(formData.month) &&
             Number(f.year) === Number(formData.year)
    );

    if (targetMatch) {
      // Switch form state into safe active data correction update mode
      setIsEditMode(true);
      setFormData({
        id: targetMatch.id,
        student_id: targetMatch.student_id,
        month: targetMatch.month,
        year: targetMatch.year,
        amount: targetMatch.amount,
        payment_date: targetMatch.payment_date,
        payment_mode: targetMatch.payment_mode || "cash",
        remarks: targetMatch.remarks || "",
        status: targetMatch.status || "paid"
      });
      toastr.info("Existing statement matched. Mode set to Update.");
    } else {
      // Set parameters back to clean collection profile routing map
      setIsEditMode(false);
      setFormData((prev) => ({
        ...prev,
        id: null,
        student_id: studentId,
      }));
    }
  };

  /* =====================================================
     MUTATION HANDLERS
  ===================================================== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // If user mutates month/year, check if an existing record matches the new period parameters
      if ((name === "month" || name === "year") && prev.student_id) {
        setTimeout(() => handleStudentSelection(prev.student_id), 10);
      }
      return updated;
    });
  };

  const handleReset = () => {
    setFormData(initialFormState);
    setIsEditMode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.student_id || !formData.amount) {
      toastr.warning("Student allocation and dynamic amount metrics are required");
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode) {
        // Direct Action Routing to update Thunk
        await dispatch(updateFee({ id: formData.id, feeData: formData })).unwrap();
        toastr.success("Fee Ledger Record Updated Successfully");
      } else {
        // Direct Action Routing to create transaction Thunk
        await dispatch(collectFee(formData)).unwrap();
        toastr.success("Fee Collection Transaction Processed Successfully");
      }

      handleReset();
      dispatch(fetchFees({ page: 1, limit: 200, search: "" })); // Update background matching pool index
    } catch (error) {
      toastr.error(error || "Processing Transaction Engine Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mnc-page-bg py-4 py-md-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">

            {/* WORKBENCH CARD */}
            <div className="mnc-card shadow-sm border">

              {/* MASTER HEADER BLOCK */}
              <div className="mnc-card-header border-bottom p-3 p-md-4 bg-white">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div>
                    <span className={`badge-mnc uppercase tracking-wider mb-2 d-inline-block ${isEditMode ? "bg-warning-light text-warning-dark" : "bg-primary-light text-primary-dark"}`}>
                      {isEditMode ? "Ledger Correction Active" : "Finance Ledger Input"}
                    </span>
                    <h4 className="fw-800 text-dark mb-1">Fee Processing Terminal</h4>
                    <p className="text-muted small mb-0">
                      {isEditMode ? "Modifying existing registered transaction statement." : "Register incoming educational transactions and clear institutional invoices."}
                    </p>
                  </div>
                  {isEditMode && (
                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill px-3 fw-700 flex-shrink-0" onClick={handleReset}>
                      Switch to Collect New
                    </button>
                  )}
                </div>
              </div>

              {/* ACTION TRANSACTION FORM */}
              <form onSubmit={handleSubmit} className="p-3 p-md-4 bg-white">
                <div className="row g-3 g-md-4">

                  {/* 1. STUDENT ACADEMIC DROPDOWN */}
                  <div className="col-12">
                    <label className="mnc-label">Student Target Profile *</label>
                    <StudentCombobox
                      students={students}
                      value={formData.student_id}
                      onSelect={handleStudentSelection}
                      disabled={isSubmitting}
                    />
                    {selectedStudent && (
                      <div className="mnc-selected-chip mt-2">
                        <span className="mnc-selected-chip-dot" />
                        {selectedStudent.name} · {selectedStudent.admission_number}
                      </div>
                    )}
                  </div>

                  {/* 2. TERM ALLOCATION MONTH */}
                  <div className="col-6 col-md-6">
                    <label className="mnc-label">Statement Month *</label>
                    <select
                      name="month"
                      className="mnc-field fw-600"
                      value={formData.month}
                      onChange={handleChange}
                    >
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 3. TERM ALLOCATION YEAR */}
                  <div className="col-6 col-md-6">
                    <label className="mnc-label">Statement Year *</label>
                    <input
                      type="number"
                      name="year"
                      className="mnc-field fw-600"
                      value={formData.year}
                      onChange={handleChange}
                      placeholder="e.g. 2026"
                      inputMode="numeric"
                    />
                  </div>

                  {/* 4. SETTLEMENT AMOUNT */}
                  <div className="col-12 col-md-6">
                    <label className="mnc-label">Collection Amount (₹) *</label>
                    <div className="input-group-mnc">
                      <span className="input-icon-prefix">₹</span>
                      <input
                        type="number"
                        name="amount"
                        className="mnc-field ps-5 fw-700 text-success"
                        value={formData.amount}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0"
                        inputMode="decimal"
                      />
                    </div>
                  </div>

                  {/* 5. TRANSACTION PAYMENT DATE */}
                  <div className="col-12 col-md-6">
                    <label className="mnc-label">Transaction Settlement Date</label>
                    <input
                      type="date"
                      name="payment_date"
                      className="mnc-field fw-600"
                      value={formData.payment_date}
                      onChange={handleChange}
                    />
                  </div>

                  {/* 6. SETTLEMENT MODE */}
                  <div className="col-12">
                    <label className="mnc-label">Payment Channel Mode</label>
                    <div className="mnc-mode-grid">
                      {[
                        { value: "cash", label: "Cash", hint: "Counter collection" },
                        { value: "upi", label: "UPI", hint: "Instant verification" },
                        { value: "bank", label: "Bank Transfer", hint: "Core institutional" },
                        { value: "card", label: "Card / POS", hint: "Point of sale" },
                      ].map((mode) => (
                        <button
                          type="button"
                          key={mode.value}
                          className={`mnc-mode-btn ${formData.payment_mode === mode.value ? "is-active" : ""}`}
                          onClick={() => handleChange({ target: { name: "payment_mode", value: mode.value } })}
                        >
                          <span className="mnc-mode-btn-label">{mode.label}</span>
                          <span className="mnc-mode-btn-hint">{mode.hint}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 7. SYSTEM AUDIT REMARKS */}
                  <div className="col-12">
                    <label className="mnc-label">Administrative Notes & Audit Remarks</label>
                    <textarea
                      rows="3"
                      name="remarks"
                      className="mnc-field"
                      value={formData.remarks}
                      onChange={handleChange}
                      placeholder="Input any additional voucher tracking notes or bank reference numbers here..."
                    />
                  </div>

                </div>

                {/* WORKBENCH FOOTER METRICS CONTROLS */}
                <div className="mnc-footer d-flex flex-wrap justify-content-end gap-2 gap-md-3 mt-4 pt-3 border-top bg-white">
                  <button
                    type="button"
                    className="btn-mnc-discard"
                    onClick={handleReset}
                    disabled={isSubmitting}
                  >
                    Clear Changes
                  </button>
                  <button
                    type="submit"
                    className={`btn-mnc-sync ${isEditMode ? "btn-mnc-update" : ""}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Syncing Ledger...</>
                    ) : isEditMode ? (
                      "Update Ledger Record"
                    ) : (
                      "Save Ledger Record"
                    )}
                  </button>
                </div>
              </form>

            </div>

          </div>
        </div>
      </div>

      <style>{`
        .mnc-page-bg {
          background: #f4f7fa;
          min-height: 100vh;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .fw-800 { font-weight: 800; }
        .fw-700 { font-weight: 700; }
        .fw-600 { font-weight: 600; }
        .uppercase { text-transform: uppercase; }

        .badge-mnc {
          font-size: 0.65rem;
          font-weight: 800;
          padding: 4px 10px;
          border-radius: 6px;
          letter-spacing: 0.05em;
        }
        .bg-primary-light { background: #e0e7ff; color: #4f46e5; }
        .bg-warning-light { background: #fef3c7; color: #d97706; }

        .mnc-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.03);
        }

        .btn-mnc-sync {
          background: #6366f1;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 12px 28px;
          font-weight: 700;
          font-size: 0.85rem;
          transition: background-color 0.2s ease;
          min-height: 44px;
        }
        .btn-mnc-sync:hover:not(:disabled) { background: #4f46e5; }

        .btn-mnc-sync.btn-mnc-update {
          background: #d97706;
        }
        .btn-mnc-sync.btn-mnc-update:hover:not(:disabled) {
          background: #b45309;
        }

        .btn-mnc-discard {
          background: white;
          border: 1px solid #e2e8f0;
          color: #64748b;
          border-radius: 8px;
          padding: 12px 24px;
          font-weight: 700;
          font-size: 0.85rem;
          min-height: 44px;
        }
        .btn-mnc-discard:hover:not(:disabled) { background: #f8fafc; }

        .mnc-label {
          display: block;
          margin-bottom: 6px;
          font-size: .68rem;
          font-weight: 800;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .mnc-field {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          padding: 11px 14px;
          font-size: 0.88rem;
          color: #334155;
          background-color: #fff;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          min-height: 44px;
        }
        .mnc-field:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
        }

        .input-group-mnc { position: relative; width: 100%; }
        .input-icon-prefix {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-weight: 700;
          color: #94a3b8;
          pointer-events: none;
        }

        /* ===== Searchable student combobox ===== */
        .mnc-combobox { position: relative; width: 100%; }
        .mnc-combobox-field { position: relative; display: flex; align-items: center; }
        .mnc-combobox-icon {
          position: absolute;
          left: 14px;
          color: #94a3b8;
          pointer-events: none;
        }
        .mnc-combobox-input { padding-left: 38px; padding-right: 34px; }
        .mnc-combobox-field.is-open .mnc-combobox-input {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
        }
        .mnc-combobox-clear {
          position: absolute;
          right: 8px;
          width: 26px;
          height: 26px;
          border: none;
          background: #f1f5f9;
          color: #64748b;
          border-radius: 50%;
          font-size: 1rem;
          line-height: 1;
          cursor: pointer;
        }
        .mnc-combobox-clear:hover { background: #e2e8f0; }

        .mnc-combobox-list {
          position: absolute;
          z-index: 40;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          max-height: 260px;
          overflow-y: auto;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 12px 28px -8px rgba(15, 23, 42, 0.18);
          margin: 0;
          padding: 6px;
          list-style: none;
        }
        .mnc-combobox-option {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 7px;
          cursor: pointer;
          font-size: 0.86rem;
          color: #334155;
        }
        .mnc-combobox-option-name { font-weight: 600; }
        .mnc-combobox-option-adm { font-size: 0.74rem; color: #94a3b8; font-weight: 600; white-space: nowrap; }
        .mnc-combobox-option.is-active { background: #eef2ff; }
        .mnc-combobox-option.is-selected .mnc-combobox-option-name { color: #4f46e5; }
        .mnc-combobox-empty {
          padding: 14px 12px;
          font-size: 0.82rem;
          color: #94a3b8;
          text-align: center;
        }

        .mnc-selected-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.78rem;
          font-weight: 700;
          color: #4f46e5;
          background: #eef2ff;
          padding: 6px 12px;
          border-radius: 999px;
        }
        .mnc-selected-chip-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #4f46e5;
        }

        /* ===== Payment mode segmented picker ===== */
        .mnc-mode-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }
        .mnc-mode-btn {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
          border: 1px solid #cbd5e1;
          background: #fff;
          border-radius: 8px;
          padding: 10px 12px;
          text-align: left;
          cursor: pointer;
          transition: border-color 0.15s ease, background-color 0.15s ease;
          min-height: 44px;
        }
        .mnc-mode-btn-label { font-size: 0.84rem; font-weight: 700; color: #334155; }
        .mnc-mode-btn-hint { font-size: 0.7rem; color: #94a3b8; font-weight: 600; }
        .mnc-mode-btn:hover { border-color: #a5b4fc; }
        .mnc-mode-btn.is-active {
          border-color: #6366f1;
          background: #eef2ff;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
        }
        .mnc-mode-btn.is-active .mnc-mode-btn-label { color: #4f46e5; }

        button:disabled, select:disabled, input:disabled { opacity: 0.6; cursor: not-allowed; }

        /* ===== Mobile responsiveness ===== */
        @media (max-width: 576px) {
          .mnc-card { border-radius: 12px; }
          .mnc-card-header, form.p-3 { padding: 1rem !important; }
          h4.fw-800 { font-size: 1.1rem; }

          .mnc-footer {
            position: sticky;
            bottom: 0;
            margin-left: -1rem;
            margin-right: -1rem;
            padding-left: 1rem;
            padding-right: 1rem;
            background: #fff;
          }
          .btn-mnc-discard,
          .btn-mnc-sync {
            flex: 1 1 auto;
            justify-content: center;
            text-align: center;
          }

          .mnc-combobox-list { max-height: 45vh; }
        }

        @media (min-width: 577px) and (max-width: 767px) {
          .mnc-mode-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (min-width: 768px) {
          .mnc-mode-grid { grid-template-columns: repeat(4, 1fr); }
        }
      `}</style>
    </div>
  );
};

export default Fees;
